import { Formik, FormikProps } from "formik";
import React, { FC, useCallback } from "react";
import { useMedia } from "react-use";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import {
  AvailabilitySectionForm,
  validationAvailabilitySectionSchema,
} from "@/views/components/ConsultantProfile/Sections/AvailabilitySectionForm";
import { Messenger } from "@/views/components/Messenger";

import { IProfileFormikProps, profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { WorkPreferencesSectionEdit } from "../WorkPreferencesSection";

const AVAILABILITY_MESSENGER_CONTEXT = "availability-form";

const availabilitySectionValidationSchema = validationAvailabilitySectionSchema;

interface IAvailabilitySectionFormValues
  extends Pick<IConsultantProfile, "availability" | "availabilityDate" | "workPreferences"> {}

interface IAvailabilitySectionFormProps extends IEditableSectionRenderProps {
  source: IConsultantProfile;
  form: FormikProps<IAvailabilitySectionFormValues>;
}

const AvailabilitySectionFormComponent: FC<IAvailabilitySectionFormProps> = ({ form, editableProps }) => {
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  useEditableSubmit(form, editableProps);

  const handleWorkPreferencesChange = useCallback(() => {
    form.setFieldTouched("workPreferences");
  }, [form.setFieldTouched]);

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="md">
        <Messenger context={AVAILABILITY_MESSENGER_CONTEXT} />

        <Flex direction={isSmallScreen ? "column" : "row"} innerMargin="md">
          <Flex width={isSmallScreen ? 12 : 6}>
            <AvailabilitySectionForm
              availability={form.values.availability}
              availabilityDate={form.values.availabilityDate}
              name="availability"
              direction="column"
              setAvailabilityDateField={(date: string | null) => form.setFieldValue("availabilityDate", date)}
              size="md"
            />
          </Flex>

          <Flex width={isSmallScreen ? 12 : 6}>
            <WorkPreferencesSectionEdit
              workPreferences={form.values.workPreferences}
              onChange={handleWorkPreferencesChange}
            />
          </Flex>
        </Flex>
      </Flex>
    </FormikForm>
  );
};

interface IAvailabilitySectionFormikProps
  extends IProfileFormikProps<IAvailabilitySectionFormValues>,
    IAvailabilitySectionFormProps {}
const AvailabilitySectionFormik: FC<IAvailabilitySectionFormikProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  ...restProps
}) => (
  <Formik
    initialValues={initialValues}
    validationSchema={validationSchema}
    onSubmit={onSubmit}
    render={(form: FormikProps<IAvailabilitySectionFormValues>) => (
      <AvailabilitySectionFormComponent {...restProps} form={form} />
    )}
  />
);
export const AvailabilitySectionEdit = profilePartialUpdaterBuilder(AvailabilitySectionFormik, {
  messengerContext: AVAILABILITY_MESSENGER_CONTEXT,
  validationSchema: availabilitySectionValidationSchema,
});
